// Kompakter sfxr/jsfxr-Generator: Rezept-Parameter (0..1 bzw. -1..1) -> Float32-Samples (44,1 kHz).
export type SfxrParams = {
  wave_type: number;
  p_env_attack: number;
  p_env_sustain: number;
  p_env_punch: number;
  p_env_decay: number;
  p_base_freq: number;
  p_freq_limit: number;
  p_freq_ramp: number;
  p_freq_dramp: number;
  p_vib_strength: number;
  p_vib_speed: number;
  p_arp_mod: number;
  p_arp_speed: number;
  p_duty: number;
  p_duty_ramp: number;
  p_repeat_speed: number;
  p_pha_offset: number;
  p_pha_ramp: number;
  p_lpf_freq: number;
  p_lpf_ramp: number;
  p_lpf_resonance: number;
  p_hpf_freq: number;
  p_hpf_ramp: number;
  sound_vol: number;
};

export const SFXR_RATE = 44100;

export function sfxrGenerate(ps: SfxrParams): Float32Array {
  const out: number[] = [];
  let fperiod = 0, fmaxperiod = 0, fslide = 0, fdslide = 0, period = 0, squareDuty = 0, squareSlide = 0;
  let arpMod = 0, arpTime = 0, arpLimit = 0;
  const reset = (): void => {
    fperiod = 100 / (ps.p_base_freq * ps.p_base_freq + 0.001);
    fmaxperiod = 100 / (ps.p_freq_limit * ps.p_freq_limit + 0.001);
    fslide = 1 - Math.pow(ps.p_freq_ramp, 3) * 0.01;
    fdslide = -Math.pow(ps.p_freq_dramp, 3) * 0.000001;
    squareDuty = 0.5 - ps.p_duty * 0.5;
    squareSlide = -ps.p_duty_ramp * 0.00005;
    arpMod = ps.p_arp_mod >= 0 ? 1 - Math.pow(ps.p_arp_mod, 2) * 0.9 : 1 + Math.pow(ps.p_arp_mod, 2) * 10;
    arpTime = 0;
    arpLimit = ps.p_arp_speed === 1 ? 0 : Math.floor(Math.pow(1 - ps.p_arp_speed, 2) * 20000 + 32);
  };
  reset();
  let fltp = 0, fltdp = 0;
  let fltw = Math.pow(ps.p_lpf_freq, 3) * 0.1;
  const fltwD = 1 + ps.p_lpf_ramp * 0.0001;
  let fltdmp = 5 / (1 + Math.pow(ps.p_lpf_resonance, 2) * 20) * (0.01 + fltw);
  if (fltdmp > 0.8) fltdmp = 0.8;
  let fltphp = 0;
  let flthp = Math.pow(ps.p_hpf_freq, 2) * 0.1;
  const flthpD = 1 + ps.p_hpf_ramp * 0.0003;
  let vibPhase = 0;
  const vibSpeed = Math.pow(ps.p_vib_speed, 2) * 0.01;
  const vibAmp = ps.p_vib_strength * 0.5;
  const envLen = [
    Math.floor(ps.p_env_attack * ps.p_env_attack * 100000),
    Math.floor(ps.p_env_sustain * ps.p_env_sustain * 100000),
    Math.floor(ps.p_env_decay * ps.p_env_decay * 100000),
  ];
  const envPunch = ps.p_env_punch;
  let envStage = 0, envTime = 0, envVol = 0;
  let fphase = Math.pow(ps.p_pha_offset, 2) * 1020;
  if (ps.p_pha_offset < 0) fphase = -fphase;
  let fdphase = Math.pow(ps.p_pha_ramp, 2) * 1;
  if (ps.p_pha_ramp < 0) fdphase = -fdphase;
  let iphase = Math.abs(Math.floor(fphase));
  let ipp = 0;
  const phaserBuf = new Float32Array(1024);
  const noiseBuf = new Float32Array(32);
  for (let i = 0; i < 32; i++) noiseBuf[i] = Math.random() * 2 - 1;
  let repTime = 0;
  const repLimit = ps.p_repeat_speed === 0 ? 0 : Math.floor(Math.pow(1 - ps.p_repeat_speed, 2) * 20000 + 32);
  let phase = 0;
  const vol = Math.max(0, Math.min(1, ps.sound_vol));
  const maxSamples = SFXR_RATE * 3;
  for (let t = 0; t < maxSamples * 8; t++) {
    if (repLimit && ++repTime >= repLimit) {
      repTime = 0;
      reset();
    }
    arpTime++;
    if (arpLimit && arpTime >= arpLimit) {
      arpLimit = 0;
      fperiod *= arpMod;
    }
    fslide += fdslide;
    fperiod *= fslide;
    if (fperiod > fmaxperiod) {
      fperiod = fmaxperiod;
      if (ps.p_freq_limit > 0) break;
    }
    let rfperiod = fperiod;
    if (vibAmp > 0) {
      vibPhase += vibSpeed;
      rfperiod = fperiod * (1 + Math.sin(vibPhase) * vibAmp);
    }
    period = Math.max(8, Math.floor(rfperiod));
    squareDuty = Math.min(0.5, Math.max(0, squareDuty + squareSlide));
    envTime++;
    if (envTime > envLen[envStage]!) {
      envTime = 0;
      envStage++;
      if (envStage > 2) break;
    }
    const el = envLen[envStage]! || 1;
    if (envStage === 0) envVol = envTime / el;
    else if (envStage === 1) envVol = 1 + Math.pow(1 - envTime / el, 1) * 2 * envPunch;
    else envVol = 1 - envTime / el;
    fphase += fdphase;
    iphase = Math.min(1023, Math.abs(Math.floor(fphase)));
    if (flthpD !== 1) flthp = Math.min(0.1, Math.max(0.00001, flthp * flthpD));
    let ssample = 0;
    for (let si = 0; si < 8; si++) {
      phase++;
      if (phase >= period) {
        phase %= period;
        if (ps.wave_type === 3) for (let i = 0; i < 32; i++) noiseBuf[i] = Math.random() * 2 - 1;
      }
      const fp = phase / period;
      let sample: number;
      switch (ps.wave_type) {
        case 0:
          sample = fp < squareDuty ? 0.5 : -0.5;
          break;
        case 1:
          sample = 1 - fp * 2;
          break;
        case 2:
          sample = Math.sin(fp * 2 * Math.PI);
          break;
        default:
          sample = noiseBuf[Math.floor(phase * 32 / period) % 32]!;
      }
      const pp = fltp;
      fltw = Math.min(0.1, Math.max(0, fltw * fltwD));
      if (ps.p_lpf_freq !== 1) {
        fltdp += (sample - fltp) * fltw;
        fltdp -= fltdp * fltdmp;
      } else {
        fltp = sample;
        fltdp = 0;
      }
      fltp += fltdp;
      fltphp += fltp - pp;
      fltphp -= fltphp * flthp;
      sample = fltphp;
      phaserBuf[ipp & 1023] = sample;
      sample += phaserBuf[(ipp - iphase + 1024) & 1023]!;
      ipp = (ipp + 1) & 1023;
      ssample += sample * envVol;
    }
    ssample = (ssample / 8) * vol * 0.5;
    out.push(Math.max(-1, Math.min(1, ssample)));
    if (out.length >= maxSamples) break;
  }
  return Float32Array.from(out);
}
